import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'
import { AggregatedInvoice } from '../types'
import { invoice } from '../ports'
import { useEffect, useState } from 'react'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

export const useAggregatedInvoice = (
  invoiceId: string | undefined
): {
  invoice: AggregatedInvoice | null
  isLoading: boolean
  invoiceIsLoading: boolean
  invoiceNotFound: boolean
  invoiceIsError: boolean
} => {
  const enabled = !!invoiceId

  const [aggregatedInvoice, setAggregatedInvoice] =
    useState<AggregatedInvoice | null>(null)

  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    {
      id: invoiceId ?? ''
    },
    {
      enabled
    }
  )

  const lineItemsQuery = useQuery(
    dashboardv99990101Client.getAllLineItems,
    {
      invoiceId: invoiceId ?? ''
    },
    {
      enabled: enabled && !!invoiceQuery.data
    }
  )

  const groupsQuery = useQuery(
    dashboardv99990101Client.getAllLineItemGroups,
    {
      invoiceId: invoiceId ?? ''
    },
    {
      enabled: enabled && !!invoiceQuery.data
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: invoiceQuery.data?.customerId ?? ''
    },
    {
      enabled: enabled && !!invoiceQuery.data
    }
  )

  const subAccountUsageQuery = useQuery(
    dashboard20240730Client.getInvoiceUsageItemGroups,
    { invoiceId: invoiceId ?? '' },
    {
      enabled: enabled && !!invoiceQuery.data
    }
  )

  // If we don't wait for all data to settle, we end up having flashes of old data when one part of
  // the aggregate is returned from the API and the others are pending.
  useEffect(() => {
    if (
      !enabled ||
      !invoiceQuery.data ||
      !groupsQuery.data ||
      !lineItemsQuery.data ||
      !customerQuery.data ||
      !subAccountUsageQuery.data ||
      invoiceQuery.isRefetching ||
      groupsQuery.isRefetching ||
      lineItemsQuery.isRefetching ||
      customerQuery.isRefetching ||
      subAccountUsageQuery.isRefetching
    ) {
      return
    }

    setAggregatedInvoice(
      invoice.fromServer(
        invoiceQuery.data,
        groupsQuery.data.items,
        lineItemsQuery.data.items,
        customerQuery.data,
        subAccountUsageQuery.data.items
      )
    )
  }, [
    invoiceQuery.data,
    groupsQuery.data,
    lineItemsQuery.data,
    customerQuery.data,
    subAccountUsageQuery.data,
    invoiceQuery.isRefetching,
    groupsQuery.isRefetching,
    lineItemsQuery.isRefetching,
    customerQuery.isRefetching,
    subAccountUsageQuery.isRefetching,
    enabled
  ])

  return {
    invoice: aggregatedInvoice,
    invoiceIsLoading: invoiceQuery.isLoading,
    invoiceNotFound: invoiceQuery.isError && invoiceQuery.error.status === 404,
    invoiceIsError: invoiceQuery.isError && invoiceQuery.error.status !== 404,
    isLoading:
      invoiceQuery.isLoading ||
      groupsQuery.isLoading ||
      lineItemsQuery.isLoading ||
      customerQuery.isLoading ||
      subAccountUsageQuery.isLoading
  }
}
